import React, { useContext, useEffect } from "react";
import { secured } from "react-abac";
import InfiniteScroll from "react-infinite-scroll-component";
import { AnimClassEnum, AnimOrderEnum, AuthoritiesEnum, CRUDActionEnum, IntegrationTypeEnum, MembersSortByEnum, MemberTypeEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import RestrictedSection from "~/components/RestrictedSection";
import { Avatar, Badge, Button, Dropdown, Grid, Icon, InputSelect, InputText, Link, Modal, Section, Spinner, Table, Text } from "~/components-shared";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CoreContext from "~/lib/context";
import manageMemberSelectors from "~/store/manageMembers/selectors";
import useAbacExt from "~/hooks/abac";
import lists from "~/lib/lists";
import manageMemberActionCreator from "~/store/manageMembers/actions";
import InviteMembersModal from "~/components/InviteMembersModal";
import styled, { useTheme } from "styled-components";
import MemberUpdateModal from "~/components/MemberUpdateModal";
import MemberInviteCommunityModal from "~/components/MemberInviteCommunityModal";
import invitationsActionCreators from "~/store/invitations/actions";
import integrationSelectors from "~/store/integrations/selectors";
import manageRoleSelectors from "~/store/manageRoles/selectors";
import rolesActionCreators from "~/store/manageRoles/actions";
import memberSelectors from "~/store/members/selectors";
import invitationSelectors from "~/store/invitations/selectors";
import { getSlackSpaceUrl } from "~/lib/helpers";
import { useHistory } from "react-router-dom";
import { useIntegratedSources } from "~/hooks/topics";
import authSelectors from "~/store/auth/selectors";
const StyledTable = styled(Table)`
  width: calc(100% + 112px);
`;
const scrollPaneStyle = {
  overflow: "initial",
  height: "initial"
};
const MemberManagement = () => {
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    setSidebar
  } = useContext(CoreContext);
  const members = useSelector(manageMemberSelectors.getMembers);
  const memberAction = useSelector(memberSelectors.getAction);
  const inviteAction = useSelector(invitationSelectors.getAction);
  const action = useSelector(manageMemberSelectors.getAction);
  const status = useSelector(manageMemberSelectors.getStatus);
  const sort = useSelector(manageMemberSelectors.getSort);
  const filter = useSelector(manageMemberSelectors.getFilter);
  const roles = useSelector(manageRoleSelectors.getRoles);
  const rolesStatus = useSelector(manageRoleSelectors.getStatus);
  const {
    hasAfter
  } = useSelector(manageMemberSelectors.getPagination);
  const {
    userHas,
    userHasOneOf
  } = useAbacExt();
  const columnCount = 11;
  const sources = useIntegratedSources();
  const user = useSelector(authSelectors.getUser);
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  let types = [MemberTypeEnum.JOYN, ...sources.map(source => lists.members.types.fromSource[source])];
  types = types.filter((item, pos) => types.indexOf(item) == pos);
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(manageMemberActionCreator.fetchStarted());
    }
  }, [dispatch, status]);
  useEffect(() => {
    if (rolesStatus === StatusStateEnum.IDLE) {
      dispatch(rolesActionCreators.fetchStarted());
    }
  }, [dispatch, rolesStatus]);
  useEffect(() => {
    setSidebar(undefined);
  }, [setSidebar]);
  const handleMemberClick = member => {
    history.push(user.id === member.id ? RoutesEnum.PROFILE : RoutesEnum.MEMBER.replace(":memberId", member.id));
  };
  const handleUpdate = memberId => {
    dispatch(manageMemberActionCreator.actionChanged(CRUDActionEnum.UPDATE, memberId));
  };
  const handleSort = newSort => {
    dispatch(manageMemberActionCreator.sorted(newSort));
  };
  const handleInvite = () => {
    dispatch(invitationsActionCreators.actionChanged(CRUDActionEnum.INVITE, undefined));
  };
  const handleFilter = newFilter => {
    dispatch(manageMemberActionCreator.filtered(newFilter));
  };
  const handleLoadMore = () => {
    dispatch(manageMemberActionCreator.loadedMore());
  };
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(InfiniteScroll, {
    style: scrollPaneStyle,
    dataLength: members.length || 0,
    next: handleLoadMore,
    hasMore: hasAfter,
    loader: /*#__PURE__*/React.createElement(Spinner, {
      s: {
        mt: 6
      },
      center: true
    })
  }, /*#__PURE__*/React.createElement(Text, {
    element: "h1",
    s: {
      mb: 10
    },
    className: `${AnimOrderEnum.ONE} ${AnimClassEnum.POP_RIGHT_FADE}`
  }, t("MemberManagement.members")), /*#__PURE__*/React.createElement(Grid, {
    cols: 3,
    gap: 3,
    s: {
      mb: 8
    },
    template: "1fr 170px 170px 170px 3.1rem"
  }, /*#__PURE__*/React.createElement(InputText, {
    value: filter?.query || "",
    placeholder: t("Members.search"),
    elemBefore: /*#__PURE__*/React.createElement(Icon, {
      color: theme.colors.text.secondary,
      src: "search",
      s: {
        mr: 2
      }
    }),
    color: "translucent",
    onChange: value => handleFilter({
      ...filter,
      query: value
    }),
    clearButton: true
  }), /*#__PURE__*/React.createElement(InputSelect, {
    value: filter?.typeIds || [],
    onChange: value => handleFilter({
      typeIds: value
    }),
    color: "translucent",
    placeholder: "Select types",
    options: types.map(id => ({
      id,
      value: lists.members.types.names[id]
    })),
    multiple: true
  }), /*#__PURE__*/React.createElement(InputSelect, {
    value: filter?.roleIds || [],
    onChange: value => handleFilter({
      ...filter,
      roleIds: value
    }),
    color: "translucent",
    placeholder: "Select roles",
    options: roles.map(_ref => {
      let {
        id,
        name
      } = _ref;
      return {
        id,
        value: name
      };
    }),
    multiple: true
  }), /*#__PURE__*/React.createElement(InputSelect, {
    value: filter?.status || "",
    onChange: value => handleFilter({
      ...filter,
      status: value
    }),
    color: "translucent",
    options: {
      "": t("common.userStatuses.all"),
      ...lists.members.statuses.names
    },
    dropdownProps: {
      direction: "bottomLeft"
    }
  }), userHas(AuthoritiesEnum.INVITATIONS_WRITE) && /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("Members.inviteNewMembers"),
    s: {
      p: 2.8
    },
    onClick: handleInvite,
    color: "translucent"
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "user-plus",
    scale: 5
  }))), /*#__PURE__*/React.createElement(StyledTable, {
    tableProps: {
      style: {
        minWidth: "650px"
      }
    },
    s: {
      mx: -14
    }
  }, /*#__PURE__*/React.createElement(Table.Header, null, /*#__PURE__*/React.createElement(Table.Tr, null, /*#__PURE__*/React.createElement(Table.Th, {
    width: "3.57143rem"
  }), /*#__PURE__*/React.createElement(Table.Th, {
    width: "3.57143rem"
  }), /*#__PURE__*/React.createElement(Table.Th, {
    sort: sort,
    sortBy: [MembersSortByEnum.BY_NAME_ASC, MembersSortByEnum.BY_NAME_DESC, MembersSortByEnum.BY_EMAIL_ASC, MembersSortByEnum.BY_EMAIL_DESC],
    onSort: handleSort
  }, [MembersSortByEnum.BY_EMAIL_ASC, MembersSortByEnum.BY_EMAIL_DESC].includes(sort) ? t("MemberManagement.email") : t("MemberManagement.fullName")), /*#__PURE__*/React.createElement(Table.Th, {
    sort: sort,
    sortBy: [MembersSortByEnum.BY_ROLE_ASC, MembersSortByEnum.BY_ROLE_DESC],
    onSort: handleSort
  }, t("MemberManagement.role")), /*#__PURE__*/React.createElement(Table.Th, null, t("MemberManagement.type")), /*#__PURE__*/React.createElement(Table.Th, null, t("MemberManagement.authorities")), /*#__PURE__*/React.createElement(Table.Th, null, t("MemberManagement.slackName")), /*#__PURE__*/React.createElement(Table.Th, null, t("MemberManagement.status")), userHasOneOf([AuthoritiesEnum.MANAGE_MEMBERS_WRITE]) && /*#__PURE__*/React.createElement(Table.Th, null), /*#__PURE__*/React.createElement(Table.Th, null))), /*#__PURE__*/React.createElement(Table.Body, null, !members.length && status === StatusStateEnum.LOADING ? /*#__PURE__*/React.createElement("tr", null, /*#__PURE__*/React.createElement("td", {
    colSpan: columnCount
  }, /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }))) : members.length ? /*#__PURE__*/React.createElement(React.Fragment, null, members.map(member => /*#__PURE__*/React.createElement(Table.Tr, {
    key: member.id
  }, /*#__PURE__*/React.createElement(Table.Td, null), /*#__PURE__*/React.createElement(Table.Td, {
    onClick: () => handleMemberClick(member)
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    fontSize: 3.5,
    weight: "semiBold"
  }, /*#__PURE__*/React.createElement(Avatar, {
    user: member,
    size: 12.5,
    showStatus: true,
    showEmoji: true
  }))), /*#__PURE__*/React.createElement(Table.Td, {
    onClick: () => handleMemberClick(member)
  }, /*#__PURE__*/React.createElement(Text, {
    element: "span",
    fontSize: 3.5,
    weight: "semiBold",
    display: "block"
  }, member.firstName, " ", member.lastName), /*#__PURE__*/React.createElement(Text, {
    fontSize: 3.5,
    weight: "semiBold",
    color: "textSecondary"
  }, member.email)), /*#__PURE__*/React.createElement(Table.Td, {
    onClick: () => handleMemberClick(member)
  }, /*#__PURE__*/React.createElement(Badge, {
    color: "grey"
  }, member.role.name)), /*#__PURE__*/React.createElement(Table.Td, {
    onClick: () => handleMemberClick(member)
  }, /*#__PURE__*/React.createElement(Badge, {
    color: "grey"
  }, lists.members.types.names[member.type])), /*#__PURE__*/React.createElement(Table.Td, {
    onClick: () => handleMemberClick(member)
  }, member.authorities.length), /*#__PURE__*/React.createElement(Table.Td, {
    onClick: () => handleMemberClick(member)
  }, slackIntegration && member.metadata.slackUserName && member.metadata.slackUserId ? /*#__PURE__*/React.createElement(Link, {
    href: `${getSlackSpaceUrl(slackIntegration)}/team/${member.metadata.slackUserId}`
  }, "@", member.metadata.slackUserName) : "-"), /*#__PURE__*/React.createElement(Table.Td, {
    onClick: () => handleMemberClick(member)
  }, /*#__PURE__*/React.createElement(Badge, {
    color: lists.members.statuses.colors(theme)[member.status]
  }, lists.members.statuses.shortenNames[member.status])), userHas(AuthoritiesEnum.MANAGE_MEMBERS_WRITE) && /*#__PURE__*/React.createElement(Table.Td, null, /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      },
      fullWidth: false,
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      src: "dots-vertical",
      color: theme.colors.text.primary,
      display: "block"
    })
  }, _ref2 => {
    let {
      isOpen,
      onItemClick
    } = _ref2;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHas(AuthoritiesEnum.MANAGE_MEMBERS_WRITE) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleUpdate(member.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "pencil",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")));
  })), /*#__PURE__*/React.createElement(Table.Td, null)))) : /*#__PURE__*/React.createElement(Table.Tr, null, /*#__PURE__*/React.createElement(Table.Td, {
    colSpan: columnCount,
    noHover: true,
    center: true
  }, /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    fontSize: 5,
    align: "center",
    display: "block",
    s: {
      my: 5
    }
  }, t("MemberManagement.noMembersFound"))))))), /*#__PURE__*/React.createElement(Modal, {
    open: memberAction === CRUDActionEnum.INVITE
  }, /*#__PURE__*/React.createElement(MemberInviteCommunityModal, null)), userHas(AuthoritiesEnum.MANAGE_MEMBERS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: action === CRUDActionEnum.UPDATE
  }, /*#__PURE__*/React.createElement(MemberUpdateModal, null)), userHas(AuthoritiesEnum.INVITATIONS_WRITE) && /*#__PURE__*/React.createElement(Modal, {
    open: inviteAction === CRUDActionEnum.INVITE
  }, /*#__PURE__*/React.createElement(InviteMembersModal, null)));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: AuthoritiesEnum.MANAGE_MEMBERS_READ,
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(MemberManagement), () => true);